import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Map, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import bookingBg from "@/assets/booking-popup-bg.jpg";
import propertyMap from "@/assets/maps/sons-guadalupe-map.webp";

interface GlampingBookingPopupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlampingBookingPopupModal({ open, onOpenChange }: GlampingBookingPopupModalProps) {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.min(Math.max(0.5, prev + delta), 4));
  }, []);

  const resetZoom = () => {
    setZoom(1);
    if (mapContainerRef.current) {
      mapContainerRef.current.scrollTo(0, 0);
    }
  };

  const handleMapOpenChange = (mapOpen: boolean) => {
    setIsMapOpen(mapOpen);
    if (!mapOpen) {
      setZoom(1);
      setIsDragging(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      if (mapContainerRef.current) {
        setScrollStart({
          x: mapContainerRef.current.scrollLeft,
          y: mapContainerRef.current.scrollTop
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !mapContainerRef.current) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    mapContainerRef.current.scrollLeft = scrollStart.x - dx;
    mapContainerRef.current.scrollTop = scrollStart.y - dy;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (open) {
      // Load the Checkfront script
      const existingScript = document.querySelector('script[src*="checkfront.com/lib/interface"]');
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "//sonsguadalupe.checkfront.com/lib/interface--0.js";
        script.type = "text/javascript";
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
          initWidget();
        };
      } else {
        // Script already loaded, just init the widget
        setTimeout(initWidget, 100);
      }
    }
  }, [open]);

  const initWidget = () => {
    if (window.DROPLET) {
      const targetEl = document.getElementById("CHECKFRONT_WIDGET_GLAMPING");
      if (targetEl) {
        targetEl.innerHTML = '<p id="CHECKFRONT_LOADER_GLAMPING" style="background: url(\'//sonsguadalupe.checkfront.com/images/loader.gif\') left center no-repeat; padding: 5px 5px 5px 20px; color: #fff;">Searching Availability...</p>';
      }
      new window.DROPLET.Widget({
        host: "sonsguadalupe.checkfront.com",
        target: "CHECKFRONT_WIDGET_GLAMPING",
        category_id: "4",
        options: "tabs",
        style: "color: fff",
        provider: "droplet",
      }).render();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 border-0 bg-transparent"
        hideDefaultClose
      >
        <DialogTitle className="sr-only">Book Your Glamping Experience</DialogTitle>
        
        {/* Sticky close button - always visible at top right */}
        <div className="sticky top-0 z-50 flex justify-end pt-4 pr-4 pointer-events-none">
          <button
            onClick={() => onOpenChange(false)}
            className="pointer-events-auto w-10 h-10 md:w-10 md:h-10 rounded-full bg-charcoal/90 hover:bg-charcoal flex items-center justify-center transition-colors shadow-lg border-2 border-white/30"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {/* Background with smokey overlay */}
        <div 
          className="relative rounded-lg overflow-hidden"
          style={{ 
            backgroundImage: `url(${bookingBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Smokey overlay */}
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" />
          
          {/* Content */}
          <div className="relative z-10 p-6 md:p-8">
            <h2 className="font-lora text-2xl md:text-3xl font-bold text-white text-center mb-6">
              Book Your Glamping Experience
            </h2>
            
            {/* View Property Map Button */}
            <div className="flex justify-center mb-6">
              <Button
                variant="outline"
                onClick={() => setIsMapOpen(true)}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
              >
                <Map className="w-4 h-4 mr-2" />
                View Property Map
              </Button>
            </div>
            
            {/* Checkfront Widget Container */}
            <div className="bg-transparent p-4 md:p-6 min-h-[400px]">
              <div id="CHECKFRONT_WIDGET_GLAMPING">
                <p 
                  id="CHECKFRONT_LOADER_GLAMPING" 
                  style={{ 
                    background: "url('//sonsguadalupe.checkfront.com/images/loader.gif') left center no-repeat", 
                    padding: "5px 5px 5px 20px",
                    color: "#fff"
                  }}
                >
                  Searching Availability...
                </p>
              </div>
              <noscript>
                <a 
                  href="https://sonsguadalupe.checkfront.com/reserve/" 
                  style={{ fontSize: "16px", color: "#fff" }}
                >
                  Continue to Secure Booking System »
                </a>
              </noscript>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Property Map Dialog */}
      <Dialog open={isMapOpen} onOpenChange={handleMapOpenChange}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl [&>button]:text-white [&>button]:opacity-100 [&>button>svg]:h-5 [&>button>svg]:w-5">
          <DialogTitle className="sr-only">Son's Guadalupe Property Map</DialogTitle>
          <div className="absolute top-12 right-4 flex flex-col gap-2 z-10">
            <Button
              size="icon"
              variant="secondary"
              onClick={() => setZoom(prev => Math.min(prev + 0.25, 4))}
              className="h-8 w-8"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.5))}
              className="h-8 w-8"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              onClick={resetZoom}
              className="h-8 w-8"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-white/70 text-center mb-2">
            Scroll to zoom{zoom > 1 ? ' • Drag to pan' : ''} • {Math.round(zoom * 100)}%
          </p>
          <div 
            ref={mapContainerRef}
            className={`overflow-auto max-h-[80vh] select-none ${zoom > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <img
              src={propertyMap}
              alt="Son's Guadalupe Property Map"
              className="rounded-lg"
              style={{ 
                width: `${zoom * 100}%`,
                minWidth: `${zoom * 100}%`,
                height: 'auto'
              }}
              draggable={false}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
